import {
    LEARNING_MODE_ADDON_CATEGORY,
    SELF_DIRECTED_LEARNING_MODE_ID
} from "@/ts/constants";

import { AddonInterface } from "@ts/interfaces/Checkout/AddonInterface";

import { sortProducts } from "@utils/utils";

type AddonLike = Pick<AddonInterface, "addonCategory" | "price"> | null;

/**
 * Minimum number of learning-mode add-ons a plan needs for the learning mode
 * selection page to be shown. The portal only emits learning_mode add-ons
 * when the feature is enabled AND the plan has at least two of them, but the
 * FE enforces the same rule defensively.
 */
export const MIN_LEARNING_MODE_OPTIONS = 2;

/** True when the add-on is a learning-mode option (Self-Directed / Guided). */
export const isLearningModeAddon = (addon: AddonLike | undefined): boolean =>
    addon?.addonCategory === LEARNING_MODE_ADDON_CATEGORY;

/**
 * True for the $0 learning-mode add-on (Self-Directed). It must never be sent
 * to Chargebee nor shown in the basket/checkout summary.
 */
export const isZeroPriceLearningMode = (
    addon: AddonLike | undefined
): boolean => isLearningModeAddon(addon) && (addon?.price ?? 0) <= 0;

/**
 * True when a selected learning mode is billable (price > 0, e.g. Guided).
 * Only billable learning modes are sent to Chargebee and rendered in the
 * basket/summary.
 */
export const isBillableLearningMode = (
    addon: Pick<AddonInterface, "price"> | null | undefined
): boolean => !!addon && addon.price > 0;

/** Learning-mode options of a plan, sorted by `order` ascending. */
export const getLearningModeAddons = (
    addons: AddonInterface[] | null | undefined
): AddonInterface[] =>
    sortProducts<AddonInterface>(
        (addons ?? []).filter(a => isLearningModeAddon(a)),
        "order"
    );

/**
 * Whether the learning mode selection page should be shown for a plan's
 * add-ons (i.e. there are at least two learning-mode options).
 */
export const shouldShowLearningModePage = (
    addons: AddonInterface[] | null | undefined
): boolean =>
    (addons ?? []).filter(a => isLearningModeAddon(a)).length >=
    MIN_LEARNING_MODE_OPTIONS;

/**
 * Resolve a learning-mode id (from a query param or saved checkout token) to
 * the plan's learning_mode add-on. The legacy `self-directed-program` id maps
 * to the plan's $0 learning-mode add-on.
 */
export const resolveLearningModeAddon = (
    learningModeId: string | null | undefined,
    addons: AddonInterface[] | null | undefined
): AddonInterface | null => {
    if (!learningModeId) {
        return null;
    }

    const options = getLearningModeAddons(addons);

    if (learningModeId === SELF_DIRECTED_LEARNING_MODE_ID) {
        return options.find(a => a.price <= 0) ?? null;
    }

    return options.find(a => a.id === learningModeId) ?? null;
};
